import {Action} from 'redux';
import Geo from 'interfaces/Geo';
import Site from 'interfaces/Site';
import Tabs from '../interfaces/Tabs';
import DevicesResponse from '../interfaces/DevicesResponse';
import InventoryDevice from '../interfaces/InventoryDevice';
import mergeSiteAndDevice from './functions/mergeSiteAndDevice';
import filterData from './functions/filterData';
import getUniqueInventoryValues from './functions/getUniqueInventoryValues';

export interface InventoryAction extends Action {
  type: string;
  payload?: any;
}

export interface FilterParams {
  geo: string[];
  brand: string[];
  siteType: string[];
  storeConcept: string[];
  siteStatus: string[];
  deviceType: string[];
  nvsnso: string[];
}

export interface InventoryState {
  selectedTab: Tabs;
  selectedGeos: Geo[];
  deviceData: InventoryDevice[];
  tableDeviceData: InventoryDevice[];
  filters: FilterParams;
  filterParams: FilterParams;
  isDeviceDataReady: boolean;
}

const DEFAULT_GEOS = [Geo.NA, Geo.EMEA, Geo.GC, Geo.APLA];

//#region State
export const initialState: InventoryState = {
  selectedTab: Tabs.CUSTOM,
  selectedGeos: [],
  deviceData: [],
  tableDeviceData: [],
  filters: {
    brand: [],
    geo: ['NA', 'EMEA', 'GC', 'APLA'],
    siteType: [],
    storeConcept: [],
    siteStatus: [],
    deviceType: [],
    nvsnso: [],
  },
  filterParams: {
    brand: [],
    geo: [],
    siteType: [],
    storeConcept: [],
    siteStatus: [],
    deviceType: [],
    nvsnso: [],
  },
  isDeviceDataReady: false,
};
//#endregion State

//#region Reducer
export const reducer = (state: InventoryState, action: InventoryAction): InventoryState => {
  switch (action.type) {
    case 'SELECT_TAB': {
      const selectedTab = action.payload;
      let selectedGeos = state.filterParams.geo as Geo[];

      if (selectedTab === Tabs.CUSTOM && selectedGeos.length === 0) {
        selectedGeos = DEFAULT_GEOS;
      } else if (selectedTab !== Tabs.CUSTOM) {
        selectedGeos = [selectedTab as Geo];
      }

      const tableDeviceData = filterData({
        selectedGeos,
        selectedFilters: state.filterParams,
        deviceData: state.deviceData,
      });

      return {
        ...state,
        selectedTab,
        selectedGeos,
        tableDeviceData,
      };
    }

    case 'UPDATE_SELECTED_FILTERS': {
      const filterParams = action.payload;

      let selectedGeos = [...filterParams.geo];

      if (state.selectedTab === Tabs.CUSTOM && selectedGeos.length === 0) {
        selectedGeos = DEFAULT_GEOS;
      } else if (state.selectedTab !== Tabs.CUSTOM) {
        const tabAsGeo = [(state.selectedTab as unknown) as Geo];
        selectedGeos = tabAsGeo;
      }

      const tableDeviceData = filterData({
        selectedGeos,
        selectedFilters: filterParams,
        deviceData: state.deviceData,
      });

      return {
        ...state,
        selectedGeos,
        tableDeviceData,
        filterParams,
      };
    }

    case 'UPDATE_DEVICE_DATA': {
      const {deviceData, sites} = action.payload;
      const mergedDeviceData: InventoryDevice[] = mergeSiteAndDevice({
        devicesResponse: deviceData,
        sites: sites,
      });

      const sortedMergedData = mergedDeviceData?.sort((a, b) => {
        const siteCodeA = a.siteCode.toString();
        const siteCodeB = b.siteCode.toString();

        if (isNaN(Number(siteCodeA)) && isNaN(Number(siteCodeB))) {
          if (siteCodeA < siteCodeB) {
            return -1;
          }
          if (siteCodeA > siteCodeB) {
            return 1;
          }
          return 0;
        } else if (isNaN(Number(siteCodeA))) {
          return 1;
        } else if (isNaN(Number(siteCodeB))) {
          return -1;
        } else {
          return Number(siteCodeA) - Number(siteCodeB);
        }
      });

      const uniqueValues: FilterParams = getUniqueInventoryValues(sortedMergedData);

      const tableDeviceData = filterData({
        selectedGeos: state.selectedGeos,
        selectedFilters: state.filterParams,
        deviceData: sortedMergedData,
      });

      return {
        ...state,
        tableDeviceData,
        filters: {
          geo: uniqueValues.geo,
          brand: uniqueValues.brand,
          siteType: uniqueValues.siteType,
          storeConcept: uniqueValues.storeConcept,
          siteStatus: uniqueValues.siteStatus,
          deviceType: uniqueValues.deviceType,
          nvsnso: uniqueValues.nvsnso,
        },
        deviceData: sortedMergedData,
        isDeviceDataReady: true,
      };
    }

    default: {
      return state;
    }
  }
};
//#endregion Reducer

//#region actions
export function actionSelectTab(tab: Tabs): InventoryAction {
  return {
    type: 'SELECT_TAB',
    payload: tab,
  };
}

export function actionUpdateSelectedFilters(filterParams: FilterParams): InventoryAction {
  return {
    type: 'UPDATE_SELECTED_FILTERS',
    payload: filterParams,
  };
}

export function actionUpdateDeviceData(
  deviceData: DevicesResponse[],
  sites: Site[]
): InventoryAction {
  return {
    type: 'UPDATE_DEVICE_DATA',
    payload: {deviceData, sites},
  };
}
//#endregion actions
